import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfileModal.css';
import { useGlobalState } from '../context/GlobalState';
import { deusSocket, sendWithAuth } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

const Profile = () => {
  const { user, setUser, userSignOut, saveProfileIsOpen, setSaveProfileIsOpen } = useGlobalState();
  const [isLoading, setIsLoading] = useState(false);
  const [tempUsername, setTempUsername] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const navigate = useNavigate();

  // Memoize the socket event handlers to prevent unnecessary re-renders
  const handleUpdateResponse = useCallback(async (userData) => {
    const userClone = { ...user };
    for (const key in userData) {
      userClone[key] = userData[key];
    }
    setUser(userClone);
    setIsLoading(false);
    alert('Save Successful!');
    setSaveProfileIsOpen(false);
  }, [user, setUser, setSaveProfileIsOpen]);

  const handleFailure = useCallback((err) => {
    alert(err.message);
    setIsLoading(false);
  }, []);

  const handleError = useCallback((err) => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    deusSocket.on('deliverUpdateProfileRes', handleUpdateResponse);
    deusSocket.on('updateUserFailure', handleFailure);
    deusSocket.on('error-event', handleError);

    return () => {
      deusSocket.off('deliverUpdateProfileRes', handleUpdateResponse);
      deusSocket.off('updateUserFailure', handleFailure);
      deusSocket.off('error-event', handleError);
    };
  }, [handleUpdateResponse, handleFailure, handleError]);

  useEffect(() => {
    if (user) {
      setTempUsername(user.username || '');
      setTempEmail(user.email || '');
    } else {
      setTempUsername('');
      setTempEmail('');
    }
    setTempPassword('');
  }, [user]);

  const hasChanges = () => {
    // Check if any field has changed from the original values
    return tempUsername !== user.username ||
           tempPassword.length > 0;
  };

  const saveUpdate = async () => {
    setIsLoading(true);
    const { username } = user;

    const original = { username };
    const updated = { username: tempUsername };
    const payload = { password: tempPassword };

    for (const key in updated) {
      if (original.hasOwnProperty(key) && original[key] !== updated[key]) {
        payload[key] = updated[key];
      }
    }

    if (Object.keys(payload).length === 1 && payload.password === '') {
      alert('No changes to save!');
      setIsLoading(false);
      return;
    }

    payload._id = user._id;
    if (tempPassword.length > 0) {
      payload.password = tempPassword;
    } else {
      delete payload.password;
    }

    sendWithAuth('updateUserProfile', payload);
  }

  const signOut = () => {
    userSignOut();
    navigate('/auth');
    setSaveProfileIsOpen(false);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    saveUpdate();
  };

  const renderMembershipInfo = () => {
    const membershipDetails = {
      free: {
        title: 'Free',
        features: [
          'Create 1 mind clone',
          'Unlimited interview training',
          'Unlimited chat interactions',
          'Automated agent fine-tuning',
          'Export conversations',
          'Export character profiles',
          'Create custom agent profiles',
        ]
      },
      // plus: {
      //   title: 'Starter',
      //   features: [
      //     'Create up to 5 mind clones',
      //     'Unlimited interview training',
      //     'Premium fine-tuning',
      //     'Unlimited chat interactions'
      //   ]
      // },
      pro: {
        title: 'Pro',
        features: [
          'Create unlimited mind clones',
          'Unlimited interview training',
          'Unlimited chat interactions',
          'Automated agent fine-tuning',
          'Premium fine-tuning',
          'Export conversations',
          'Export character profiles',
          'Create custom agent profiles',
        ]
      },
      // god: {
      //   title: 'God',
      //   features: [
      //     'Unlimited mind clones',
      //     'Premium fine-tuning',
      //     'Unlimited chat interactions',
      //     'Agent customization',
      //     'Priority support'
      //   ]
      // }
    };

    const currentPlan = membershipDetails[user?.membership || 'free'];

    return (
      <div className="membership-info">
        <h3>Current Plan: {currentPlan.title}</h3>
        <ul>
          {currentPlan.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
        {/* <button 
          className="upgrade-button"
          onClick={() => navigate('/pricing')}
        >
          Upgrade Plan
        </button> */}
      </div>
    );
  };

  if (!saveProfileIsOpen) return null;

  return (
    <div className="modal show">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Manage Account</h2>
          <button className="close-modal" onClick={() => setSaveProfileIsOpen(false)}>&times;</button>
        </div>
        <div className="modal-body">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <form onSubmit={handleSubmit} className="form-container">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={tempEmail}
                    onChange={(e) => setTempEmail(e.target.value)}
                    disabled
                    style={{ color: '#666666' }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input
                    id="username"
                    type="text"
                    value={tempUsername}
                    onChange={(e) => setTempUsername(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    placeholder="Enter to change password"
                  />
                </div>
                <div className="button-group">
                  <button type="submit" disabled={!hasChanges() || isLoading}>Save Changes</button>
                  <button type="button" onClick={signOut}>Sign Out</button>
                </div>
              </form>
              {renderMembershipInfo()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
